import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Phone, User, Lock, Eye, EyeOff, ArrowRight, Shield } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { Separator } from '../components/ui/Separator';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Footer } from '../components/Footer';
const COUNTRY_CODES = [{
  code: '+250',
  country: 'Rwanda 🇷🇼',
  flag: '🇷🇼'
}, {
  code: '+254',
  country: 'Kenya 🇰🇪',
  flag: '🇰🇪'
}, {
  code: '+255',
  country: 'Tanzania 🇹🇿',
  flag: '🇹🇿'
}, {
  code: '+256',
  country: 'Uganda 🇺🇬',
  flag: '🇺🇬'
}, {
  code: '+257',
  country: 'Burundi 🇧🇮',
  flag: '🇧🇮'
}];
export function Auth() {
  const {
    t
  } = useLanguage();
  const {
    login,
    signup
  } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [countryCode, setCountryCode] = useState('+250');
  const [phone, setPhone] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  // Load remembered credentials
  useEffect(() => {
    const saved = localStorage.getItem('edumarket_remembered');
    if (saved) {
      const data = JSON.parse(saved);
      setPhone(data.phone || '');
      setCountryCode(data.countryCode || '+250');
      setRemember(true);
    }
  }, []);
  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!phone || phone.replace(/\D/g, '').length !== 10) {
      errs.phone = t('auth.error.phone');
    }
    if (!password) errs.password = t('auth.error.required');
    if (mode === 'signup') {
      if (!fullName.trim()) errs.fullName = t('auth.error.required');
      if (password !== confirmPassword) errs.confirmPassword = t('auth.error.password.match');
      if (password.length < 4) errs.password = 'Password must be at least 4 characters';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const cleanPhone = phone.replace(/\D/g, '');
    try {
      if (mode === 'login') {
        const result = await login(cleanPhone, countryCode, password);
        if (result.success) {
          if (remember) {
            localStorage.setItem('edumarket_remembered', JSON.stringify({
              phone: cleanPhone,
              countryCode
            }));
          } else {
            localStorage.removeItem('edumarket_remembered');
          }
          toast.success(t('auth.success.login'));
          navigate('/marketplace');
        } else {
          toast.error(result.error || t('auth.error.invalid'));
        }
      } else {
        const result = await signup(cleanPhone, countryCode, fullName.trim(), password);
        if (result.success) {
          if (remember) {
            localStorage.setItem('edumarket_remembered', JSON.stringify({
              phone: cleanPhone,
              countryCode
            }));
          }
          toast.success(t('auth.success.signup'));
          navigate('/marketplace');
        } else {
          toast.error(result.error || 'Signup failed');
        }
      }
    } catch (error) {
      toast.error('Connection error. Please try again.');
    }
    setLoading(false);
  };
  return <div className="w-full min-h-screen flex flex-col" data-id="element-656">
      <div className="flex-1 flex items-center justify-center py-12 px-4" data-id="element-657">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="w-full max-w-md" data-id="element-658">
          <Card className="shadow-lg" data-id="element-659">
            <CardHeader className="text-center pb-2" data-id="element-660">
              <div className="mx-auto h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center mb-3" data-id="element-661">
                <User className="h-6 w-6 text-white" data-id="element-662" />
              </div>
              <CardTitle className="text-xl" data-id="element-663">
                {mode === 'login' ? t('auth.login.title') : t('auth.signup.title')}
              </CardTitle>
              <CardDescription data-id="element-664">{t('app.tagline')}</CardDescription>
            </CardHeader>

            <CardContent className="pt-4" data-id="element-665">
              <form onSubmit={handleSubmit} className="space-y-4" data-id="element-666">
                {/* Country Code + Phone */}
                <div className="space-y-2" data-id="element-667">
                  <Label data-id="element-668">{t('auth.phone')}</Label>
                  <div className="flex gap-2" data-id="element-669">
                    <Select value={countryCode} onValueChange={setCountryCode} data-id="element-670">
                      <SelectTrigger className="w-[120px]" data-id="element-671">
                        <SelectValue data-id="element-672" />
                      </SelectTrigger>
                      <SelectContent data-id="element-673">
                        {COUNTRY_CODES.map(cc => <SelectItem key={cc.code} value={cc.code} data-id="element-674">
                            {cc.flag} {cc.code}
                          </SelectItem>)}
                      </SelectContent>
                    </Select>
                    <div className="flex-1" data-id="element-675">
                      <Input type="tel" placeholder={t('auth.phone.placeholder')} value={phone} onChange={e => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                      setPhone(val);
                    }} className={errors.phone ? 'border-red-500' : ''} data-id="element-676" />
                    </div>
                  </div>
                  {errors.phone && <p className="text-xs text-red-500" data-id="element-677">{errors.phone}</p>}
                  <p className="text-xs text-muted-foreground" data-id="element-678">
                    {phone.length}/10 digits
                  </p>
                </div>

                {/* Full Name (signup only) */}
                <AnimatePresence mode="wait" data-id="element-679">
                  {mode === 'signup' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="space-y-2 overflow-hidden" data-id="element-680">
                      <Label data-id="element-681">{t('auth.fullname')}</Label>
                      <Input placeholder={t('auth.fullname.placeholder')} value={fullName} onChange={e => setFullName(e.target.value)} className={errors.fullName ? 'border-red-500' : ''} data-id="element-682" />
                      {errors.fullName && <p className="text-xs text-red-500" data-id="element-683">
                          {errors.fullName}
                        </p>}
                    </motion.div>}
                </AnimatePresence>

                {/* Password */}
                <div className="space-y-2" data-id="element-684">
                  <Label data-id="element-685">{t('auth.password')}</Label>
                  <div className="relative" data-id="element-686">
                    <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} className={errors.password ? 'border-red-500 pr-10' : 'pr-10'} data-id="element-687" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-id="element-688">
                      {showPassword ? <EyeOff className="h-4 w-4" data-id="element-689" /> : <Eye className="h-4 w-4" data-id="element-690" />}
                    </button>
                  </div>
                  {errors.password && <p className="text-xs text-red-500" data-id="element-691">{errors.password}</p>}
                </div>

                {/* Confirm Password (signup only) */}
                <AnimatePresence mode="wait" data-id="element-692">
                  {mode === 'signup' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="space-y-2 overflow-hidden" data-id="element-693">
                      <Label data-id="element-694">{t('auth.password.confirm')}</Label>
                      <Input type={showPassword ? 'text' : 'password'} placeholder="••••••••" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className={errors.confirmPassword ? 'border-red-500' : ''} data-id="element-695" />
                      {errors.confirmPassword && <p className="text-xs text-red-500" data-id="element-696">
                          {errors.confirmPassword}
                        </p>}
                    </motion.div>}
                </AnimatePresence>

                {/* Remember Me */}
                <div className="flex items-center gap-2" data-id="element-697">
                  <input type="checkbox" id="remember" checked={remember} onChange={e => setRemember(e.target.checked)} className="h-4 w-4 rounded border-border" data-id="element-698" />
                  <label htmlFor="remember" className="text-sm text-muted-foreground cursor-pointer" data-id="element-699">
                    {t('auth.remember')}
                  </label>
                </div>

                <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2" data-id="element-700">
                  {loading ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-701" /> : <ArrowRight className="h-4 w-4" data-id="element-702" />}
                  {mode === 'login' ? t('auth.login.btn') : t('auth.signup.btn')}
                </Button>
              </form>

              <Separator className="my-4" data-id="element-703" />

              <button onClick={() => {
              setMode(mode === 'login' ? 'signup' : 'login');
              setErrors({});
            }} className="w-full text-center text-sm text-blue-600 hover:underline" data-id="element-704">
                {mode === 'login' ? t('auth.switch.signup') : t('auth.switch.login')}
              </button>

              <Separator className="my-4" data-id="element-705" />

              <button onClick={() => navigate('/admin/login')} className="w-full flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors" data-id="element-706">
                <Shield className="h-3.5 w-3.5" data-id="element-707" />
                {t('admin.login.title')}
              </button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
      <Footer data-id="element-708" />
    </div>;
}